import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Share2, Trash2 } from "lucide-react";

import {
  inviteUrlFor,
  INVITE_LINK_DEFAULT_EXPIRATION_DAYS,
  INVITE_LINK_DEFAULT_MAX_ACCEPTS,
  INVITE_LINK_MAX_ACCEPTS_LIMIT,
  INVITE_LINK_MAX_EXPIRATION_DAYS,
  type InviteLink,
  type TravelerRole,
  type Trip,
} from "@travelix/core";

import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth-provider";
import {
  revokeInviteLink,
  createInviteLink,
  listInviteLinksForTrip,
  unwrap,
  queryKeys,
} from "@/lib/api";
import { useTripMutation } from "@/hooks/use-trip-mutation";

/**
 * "Share invite link" sheet in Trip Settings.
 *
 * Lists existing links with usage + revoke, and hosts an inline create form
 * so an owner or member can generate a new link without a second dialog.
 *
 * Redemption + counter bumping happen server-side (see the
 * redeemInviteLink Cloud Function); this sheet only ever creates or revokes.
 */
export function InviteLinksSheet({
  open,
  onOpenChange,
  trip,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trip: Trip;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader
          title="Share invite link"
          description="Share this link and anyone with it can join. Optional cap and expiry keep it under control."
        />
        <SheetBody className="flex flex-col gap-6">
          <CreateLinkForm trip={trip} />
          <ExistingLinks trip={trip} />
        </SheetBody>
        <SheetFooter>
          <Button variant="secondary" block onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ─── Create form ─────────────────────────────────────────────────────────────

function CreateLinkForm({ trip }: { trip: Trip }) {
  const { user } = useAuth();

  const [role, setRole] = useState<TravelerRole>("traveler");
  const [maxAccepts, setMaxAccepts] = useState<string>(
    String(INVITE_LINK_DEFAULT_MAX_ACCEPTS),
  );
  const [unlimitedSeats, setUnlimitedSeats] = useState(false);
  const [expiresDays, setExpiresDays] = useState<string>(
    String(INVITE_LINK_DEFAULT_EXPIRATION_DAYS),
  );
  const [neverExpires, setNeverExpires] = useState(false);

  const mutation = useTripMutation({
    keys: [queryKeys.inviteLinks(trip.id)],
    mutationFn: async () => {
      if (!user?.uid || !user.email) throw new Error("Not signed in");

      // Interpret the two "off" toggles as null on the server side.
      const capNum = unlimitedSeats ? null : Number(maxAccepts);
      if (
        capNum !== null &&
        (!Number.isFinite(capNum) ||
          capNum < 1 ||
          capNum > INVITE_LINK_MAX_ACCEPTS_LIMIT)
      ) {
        throw new Error(
          `Seat cap must be 1–${INVITE_LINK_MAX_ACCEPTS_LIMIT}, or unlimited.`,
        );
      }
      const daysNum = neverExpires ? null : Number(expiresDays);
      if (
        daysNum !== null &&
        (!Number.isFinite(daysNum) ||
          daysNum < 1 ||
          daysNum > INVITE_LINK_MAX_EXPIRATION_DAYS)
      ) {
        throw new Error(
          `Expiration must be 1–${INVITE_LINK_MAX_EXPIRATION_DAYS} days, or none.`,
        );
      }
      const expiresAt = daysNum === null
        ? null
        : new Date(Date.now() + daysNum * 86_400_000);

      return unwrap(createInviteLink(
        trip,
        { uid: user.uid, email: user.email },
        { role, expiresAt, maxAccepts: capNum },
      ));
    },
    successToast: "Invite link created — copied to clipboard.",
    errorToast: "Couldn't create link.",
    // Copy the fresh link to the clipboard so sharing is one step.
    onSuccess: (link) => void copyToClipboard(inviteUrlFor(link.id)),
  });

  return (
    <section aria-label="New invite link" className="flex flex-col gap-3">
      <p className="text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
        New link
      </p>

      <Field id="il-role" label="Role for joiners">
        <div role="radiogroup" className="flex gap-2">
          {(["traveler", "viewer"] as const).map((r) => (
            <button
              key={r}
              type="button"
              role="radio"
              aria-checked={role === r}
              onClick={() => setRole(r)}
              className={
                "flex-1 rounded-lg border px-3 py-2 text-[13px] font-semibold transition-colors " +
                (role === r
                  ? "border-primary bg-primary-soft/60 text-ink"
                  : "border-line text-ink-subtle hover:text-ink")
              }
            >
              {r === "traveler" ? "Traveler" : "Viewer"}
            </button>
          ))}
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field id="il-seats" label="Seat cap">
          <div className="flex items-center gap-2">
            <Input
              id="il-seats"
              type="number"
              min={1}
              max={INVITE_LINK_MAX_ACCEPTS_LIMIT}
              inputMode="numeric"
              value={unlimitedSeats ? "" : maxAccepts}
              disabled={unlimitedSeats}
              onChange={(e) => setMaxAccepts(e.target.value)}
              placeholder="—"
            />
          </div>
          <ToggleLabel
            checked={unlimitedSeats}
            onChange={setUnlimitedSeats}
            label="Unlimited"
          />
        </Field>

        <Field id="il-exp" label="Expires in (days)">
          <Input
            id="il-exp"
            type="number"
            min={1}
            max={INVITE_LINK_MAX_EXPIRATION_DAYS}
            inputMode="numeric"
            value={neverExpires ? "" : expiresDays}
            disabled={neverExpires}
            onChange={(e) => setExpiresDays(e.target.value)}
            placeholder="—"
          />
          <ToggleLabel
            checked={neverExpires}
            onChange={setNeverExpires}
            label="Never"
          />
        </Field>
      </div>

      <Button
        size="lg"
        block
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
      >
        {mutation.isPending ? "Creating…" : "Create link"}
      </Button>
    </section>
  );
}

function ToggleLabel({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="mt-1 flex items-center gap-2 text-[12px] text-ink-subtle">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5"
      />
      {label}
    </label>
  );
}

// ─── Existing list ───────────────────────────────────────────────────────────

function ExistingLinks({ trip }: { trip: Trip }) {
  const query = useQuery({
    queryKey: queryKeys.inviteLinks(trip.id),
    queryFn: async () => {
      return unwrap(listInviteLinksForTrip(trip.id));
    },
    enabled: !!trip.id,
  });

  const revoke = useTripMutation({
    keys: [queryKeys.inviteLinks(trip.id)],
    mutationFn: (token: string) => unwrap(revokeInviteLink(token)),
    successToast: "Link revoked.",
    errorToast: "Couldn't revoke link.",
  });

  if (query.isLoading) {
    return (
      <section aria-label="Existing invite links" className="flex flex-col gap-2">
        <p className="text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
          Active links
        </p>
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-14 w-full rounded-lg" />
      </section>
    );
  }

  const links = (query.data ?? []).filter((l) => !isDead(l));
  if (links.length === 0) {
    return (
      <section aria-label="Existing invite links" className="flex flex-col gap-2">
        <p className="text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
          Active links
        </p>
        <p className="text-[13px] text-ink-subtle">
          No active links yet. Create one above and share it in a group chat.
        </p>
      </section>
    );
  }

  return (
    <section aria-label="Existing invite links" className="flex flex-col gap-2">
      <p className="text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
        Active links · {links.length}
      </p>
      <ul className="flex flex-col gap-2">
        {links.map((l) => (
          <li key={l.id}>
            <LinkRow
              link={l}
              onRevoke={() => revoke.mutate(l.id)}
              revoking={revoke.isPending}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function LinkRow({
  link,
  onRevoke,
  revoking,
}: {
  link: InviteLink;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const url = inviteUrlFor(link.id);
  const seatsLabel = useMemo(() => {
    if (link.maxAccepts == null) return `${link.acceptCount} joined · no cap`;
    const left = Math.max(0, link.maxAccepts - link.acceptCount);
    return `${link.acceptCount}/${link.maxAccepts} joined · ${left} left`;
  }, [link.acceptCount, link.maxAccepts]);
  const expiryLabel = useMemo(() => {
    if (!link.expiresAt) return "no expiration";
    const ms = link.expiresAt.toMillis() - Date.now();
    if (ms <= 0) return "expired";
    const days = Math.ceil(ms / 86_400_000);
    return days <= 1 ? "expires today" : `expires in ${days}d`;
  }, [link.expiresAt]);

  return (
    <div className="rounded-lg border border-line bg-bg-elevated p-3">
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate rounded bg-bg-sunken px-2 py-1 text-[12px] text-ink-muted">
          {url}
        </code>
        <IconAction
          label="Copy"
          onClick={() => {
            void copyToClipboard(url);
            toast.success("Link copied.");
          }}
        >
          <Copy className="h-4 w-4" />
        </IconAction>
        <IconAction label="Share" onClick={() => void shareUrl(url, link.tripName)}>
          <Share2 className="h-4 w-4" />
        </IconAction>
        <IconAction
          label="Revoke"
          tone="danger"
          disabled={revoking}
          onClick={onRevoke}
        >
          <Trash2 className="h-4 w-4" />
        </IconAction>
      </div>
      <p className="mt-2 text-[12px] text-ink-subtle">
        {seatsLabel} · {expiryLabel} · {link.role}
      </p>
    </div>
  );
}

function IconAction({
  label,
  onClick,
  disabled,
  tone = "default",
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: "default" | "danger";
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={
        "flex h-8 w-8 items-center justify-center rounded-md border border-line " +
        (tone === "danger"
          ? "text-danger hover:bg-danger/10"
          : "text-ink-subtle hover:text-ink hover:bg-bg-sunken")
      }
    >
      {children}
    </button>
  );
}

// ─── Utilities ───────────────────────────────────────────────────────────────

function isDead(link: InviteLink): boolean {
  if (link.revoked) return true;
  if (link.expiresAt && link.expiresAt.toMillis() < Date.now()) return true;
  if (link.maxAccepts != null && link.acceptCount >= link.maxAccepts) return true;
  return false;
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    /* fall through to the legacy path */
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand("copy");
  } catch {
    /* nothing to do — clipboard denied */
  }
  document.body.removeChild(ta);
}

async function shareUrl(url: string, title: string): Promise<void> {
  const anyNavigator = navigator as unknown as {
    share?: (data: { url: string; title?: string; text?: string }) => Promise<void>;
  };
  if (anyNavigator.share) {
    try {
      await anyNavigator.share({ url, title: `Join ${title}`, text: `Join my trip: ${title}` });
      return;
    } catch {
      /* user cancelled — no toast */
      return;
    }
  }
  await copyToClipboard(url);
  toast.success("Share sheet unavailable — link copied instead.");
}
